/**
 * Configuration Step Component
 * 
 * Second step in Auto Fleet Generator - allows user to configure
 * shift parameters, break requirements, peak periods, and fleet size
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Slider,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Stack,
  InputAdornment,
  Paper,
} from '@mui/material';

import { ConfigurationStepProps, PeakPeriod } from './types';
import { logEvent } from '../../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
const ConfigurationStep: React.FC<ConfigurationStepProps> = ({
  config,
  onConfigChange,
  availablePoints,
  analysisResult,
}) => {
  const { t } = useTranslation('mapWizards');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    vehicle: false,
    peaks: false,
    advanced: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Calculate effective vehicle count with flexibility
  const baseVehicles = config.manualVehicleCount || 1;
  const flexibilityMultiplier = 1 + (config.flexibilityPercent / 100);
  const effectiveVehicles = Math.max(1, Math.round(baseVehicles * flexibilityMultiplier));
  const vehicleChange = effectiveVehicles - baseVehicles;

  // Handle peak period changes
  const handleAddPeakPeriod = () => {
    const newPeak: PeakPeriod = {
      start: '07:00',
      end: '09:00',
    };
    onConfigChange({
      peakPeriods: [...config.peakPeriods, newPeak],
    });

    logEvent({
      category: 'AutoFleetGenerator',
      action: 'PeakPeriodAdded',
      label: 'Peak Period Added',
      peak_count: config.peakPeriods.length + 1,
    });
  };

  const handleRemovePeakPeriod = (index: number) => {
    onConfigChange({
      peakPeriods: config.peakPeriods.filter((_, i) => i !== index),
    });

    logEvent({
      category: 'AutoFleetGenerator',
      action: 'PeakPeriodRemoved',
      label: 'Peak Period Removed',
      peak_count: config.peakPeriods.length - 1,
    });
  };

  const handlePeakPeriodChange = (index: number, field: 'start' | 'end', value: string) => {
    const newPeaks = config.peakPeriods.map((peak, i) =>
      i === index ? { ...peak, [field]: value } : peak
    );
    onConfigChange({ peakPeriods: newPeaks });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.configuration.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('autoFleetGen.configuration.description')}
      </Typography>

      {/* Fleet Flexibility Slider - Prominent placement */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Icon size={20} name="sliders" />
          <Typography variant="h6">{t('autoFleetGen.configuration.flexibility.title')}</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('autoFleetGen.configuration.flexibility.description')}
        </Typography>

        <Box sx={{ px: 2 }}>
          <Slider
            value={config.flexibilityPercent}
            onChange={(_, value) => {
              onConfigChange({ flexibilityPercent: value as number });
              logEvent({
                category: 'AutoFleetGenerator',
                action: 'FlexibilityAdjusted',
                label: `Flexibility: ${value}%`,
                value: value as number,
              });
            }}
            min={-50}
            max={100}
            step={10}
            marks={[
              { value: -50, label: '-50%' },
              { value: 0, label: t('autoFleetGen.configuration.flexibility.markBase') },
              { value: 50, label: '+50%' },
              { value: 100, label: '+100%' },
            ]}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}%`}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('autoFleetGen.configuration.flexibility.baseVehicles')}
            </Typography>
            <Typography variant="h5" color="text.primary">
              {baseVehicles}
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary">
              {t('autoFleetGen.configuration.flexibility.adjustment')}
            </Typography>
            <Typography
              variant="h5"
              color={vehicleChange > 0 ? 'success.main' : vehicleChange < 0 ? 'warning.main' : 'text.primary'}
            >
              {vehicleChange > 0 ? '+' : ''}{vehicleChange}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">
              {t('autoFleetGen.configuration.flexibility.totalVehicles')}
            </Typography>
            <Typography variant="h5" color="primary">
              {effectiveVehicles}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Basic Service Configuration */}
      <Accordion
        expanded={expandedSections.basic}
        onChange={() => toggleSection('basic')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="gear" />
            <Typography variant="subtitle1">{t('autoFleetGen.configuration.basic.title')}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Vehicle Count Override */}
            <TextField
              label={t('autoFleetGen.configuration.basic.numberOfVehicles')}
              type="number"
              fullWidth
              value={config.manualVehicleCount || ''}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value > 0) {
                  onConfigChange({
                    manualVehicleCount: value,
                    useDemandBasedAllocation: false,
                  });
                }
              }}
              inputProps={{ min: 1 }}
              helperText={
                analysisResult
                  ? t('autoFleetGen.configuration.basic.numberOfVehiclesHelperWithAnalysis', { value: analysisResult.calculatedVehicles })
                  : t('autoFleetGen.configuration.basic.numberOfVehiclesHelper')
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="bus" size={20} />
                  </InputAdornment>
                ),
              }}
            />

            <Divider />

            {/* Service Times */}
            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.configuration.basic.serviceStart')}
                type="time"
                fullWidth
                value={config.serviceStartTime}
                onChange={(e) => onConfigChange({ serviceStartTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en-GB' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="clock" size={20} />
                    </InputAdornment>
                  ),
                }}
                helperText={t('autoFleetGen.configuration.basic.serviceStartHelper')}
              />
              <TextField
                label={t('autoFleetGen.configuration.basic.serviceEnd')}
                type="time"
                fullWidth
                value={config.serviceEndTime}
                onChange={(e) => onConfigChange({ serviceEndTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ lang: 'en-GB' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="clock" size={20} />
                    </InputAdornment>
                  ),
                }}
                helperText={t('autoFleetGen.configuration.basic.serviceEndHelper')}
              />
            </Box>

            <Divider />

            {/* Shift Constraints */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.configuration.basic.shiftAndBreak')}
            </Typography>

            <TextField
              label={t('autoFleetGen.configuration.basic.maxShiftTime')}
              type="number"
              fullWidth
              value={config.maxShiftTimeMinutes}
              onChange={(e) => onConfigChange({ maxShiftTimeMinutes: parseInt(e.target.value, 10) })}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('autoFleetGen.configuration.basic.minutes')}</InputAdornment>,
              }}
              helperText={t('autoFleetGen.configuration.basic.maxShiftTimeHelper')}
            />

            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.configuration.basic.breakLength')}
                type="number"
                fullWidth
                value={config.breakLengthMinutes}
                onChange={(e) => onConfigChange({ breakLengthMinutes: parseInt(e.target.value, 10) })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.configuration.basic.minutes')}</InputAdornment>,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="coffee" size={20} />
                    </InputAdornment>
                  ),
                }}
                helperText={t('autoFleetGen.configuration.basic.breakLengthHelper')}
              />
              <TextField
                label={t('autoFleetGen.configuration.basic.minBeforeBreak')}
                type="number"
                fullWidth
                value={config.minTimeBeforeBreakMinutes}
                onChange={(e) =>
                  onConfigChange({ minTimeBeforeBreakMinutes: parseInt(e.target.value, 10) })
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.configuration.basic.min')}</InputAdornment>,
                }}
                helperText={t('autoFleetGen.configuration.basic.minBeforeBreakHelper')}
              />
              <TextField
                label={t('autoFleetGen.configuration.basic.minAfterBreak')}
                type="number"
                fullWidth
                value={config.minTimeAfterBreakMinutes}
                onChange={(e) =>
                  onConfigChange({ minTimeAfterBreakMinutes: parseInt(e.target.value, 10) })
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.configuration.basic.min')}</InputAdornment>,
                }}
                helperText={t('autoFleetGen.configuration.basic.minAfterBreakHelper')}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Vehicle Configuration */}
      <Accordion
        expanded={expandedSections.vehicle}
        onChange={() => toggleSection('vehicle')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="bus" />
            <Typography variant="subtitle1">{t('autoFleetGen.configuration.vehicle.title')}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Seat Configuration */}
            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.configuration.vehicle.standardSeats')}
                type="number"
                fullWidth
                value={config.standardSeats}
                onChange={(e) => onConfigChange({ standardSeats: parseInt(e.target.value, 10) })}
                inputProps={{ min: 0 }}
                helperText={t('autoFleetGen.configuration.vehicle.standardSeatsHelper')}
              />
              <TextField
                label={t('autoFleetGen.configuration.vehicle.wheelchairSeats')}
                type="number"
                fullWidth
                value={config.wheelchairSeats}
                onChange={(e) => onConfigChange({ wheelchairSeats: parseInt(e.target.value, 10) })}
                inputProps={{ min: 0 }}
                helperText={t('autoFleetGen.configuration.vehicle.wheelchairSeatsHelper')}
              />
            </Box>

            <Divider />

            {/* Location Configuration */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.configuration.vehicle.depotLocations')}
            </Typography>

            {availablePoints.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="body2">
                  {t('autoFleetGen.configuration.vehicle.noPoints')}
                </Typography>
              </Alert>
            ) : (
              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('autoFleetGen.configuration.vehicle.startDepot')}</InputLabel>
                  <Select
                    value={config.startLocationId}
                    onChange={(e) => onConfigChange({ startLocationId: e.target.value })}
                    label={t('autoFleetGen.configuration.vehicle.startDepot')}
                    MenuProps={{ sx: { zIndex: 2600 } }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon name="location-dot" size={20} />
                      </InputAdornment>
                    }
                  >
                    {availablePoints.map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.name || point.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>{t('autoFleetGen.configuration.vehicle.endDepot')}</InputLabel>
                  <Select
                    value={config.endLocationId}
                    onChange={(e) => onConfigChange({ endLocationId: e.target.value })}
                    label={t('autoFleetGen.configuration.vehicle.endDepot')}
                    MenuProps={{ sx: { zIndex: 2600 } }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon name="location-dot" size={20} />
                      </InputAdornment>
                    }
                  >
                    {availablePoints.map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.name || point.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Peak Periods Configuration */}
      <Accordion
        expanded={expandedSections.peaks}
        onChange={() => toggleSection('peaks')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} d={ICON_FALLBACKS.TrendingUp} />
            <Typography variant="subtitle1">{t('autoFleetGen.configuration.peaks.title')}</Typography>
            {config.peakPeriods.length > 0 && (
              <Chip label={t('autoFleetGen.configuration.peaks.countConfigured', { count: config.peakPeriods.length })} size="small" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('autoFleetGen.configuration.peaks.description')}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<Icon size={20} name="plus" />}
              onClick={handleAddPeakPeriod}
              fullWidth
            >
              {t('autoFleetGen.configuration.peaks.addButton')}
            </Button>

            {config.peakPeriods.map((peak, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label={t('autoFleetGen.configuration.peaks.startTime')}
                    type="time"
                    value={peak.start || ''}
                    onChange={(e) => handlePeakPeriodChange(index, 'start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-GB' }}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('autoFleetGen.configuration.peaks.to')}
                  </Typography>
                  <TextField
                    label={t('autoFleetGen.configuration.peaks.endTime')}
                    type="time"
                    value={peak.end || ''}
                    onChange={(e) => handlePeakPeriodChange(index, 'end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ lang: 'en-GB' }}
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleRemovePeakPeriod(index)}
                    startIcon={<Icon size={20} name="trash" />}
                  >
                    {t('autoFleetGen.configuration.peaks.remove')}
                  </Button>
                </Box>
              </Paper>
            ))}

            {config.peakPeriods.length > 0 && (
              <>
                <Divider />
                <TextField
                  label={t('autoFleetGen.configuration.peaks.breakReduction')}
                  type="number"
                  fullWidth
                  value={config.breakReductionDuringPeakMinutes}
                  onChange={(e) =>
                    onConfigChange({
                      breakReductionDuringPeakMinutes: parseInt(e.target.value, 10),
                    })
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('autoFleetGen.configuration.basic.minutes')}</InputAdornment>,
                  }}
                  helperText={t('autoFleetGen.configuration.peaks.breakReductionHelper')}
                />
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Options */}
      <Accordion
        expanded={expandedSections.advanced}
        onChange={() => toggleSection('advanced')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="sliders" />
            <Typography variant="subtitle1">{t('autoFleetGen.configuration.advanced.title')}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.staggerBreaks}
                  onChange={(e) => {
                    onConfigChange({ staggerBreaks: e.target.checked });
                    logEvent({
                      category: 'AutoFleetGenerator',
                      action: 'StaggerBreaksToggled',
                      label: e.target.checked ? 'Enabled' : 'Disabled',
                    });
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('autoFleetGen.configuration.advanced.staggerBreaks')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('autoFleetGen.configuration.advanced.staggerBreaksHelper')}
                  </Typography>
                </Box>
              }
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={config.useDemandBasedAllocation}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    
                    // Initialize hourly requirements if enabling and none exist
                    if (isEnabled && !config.hourlyVehicleRequirements) {
                      const baseCount = config.manualVehicleCount || 5;
                      const defaultRequirements: Record<number, number> = {};
                      // Initialize all hours with the base vehicle count
                      for (let hour = 0; hour < 24; hour++) {
                        defaultRequirements[hour] = baseCount;
                      }
                      onConfigChange({ 
                        useDemandBasedAllocation: true,
                        hourlyVehicleRequirements: defaultRequirements,
                      });
                    } else {
                      onConfigChange({ useDemandBasedAllocation: isEnabled });
                    }
                    
                    logEvent({
                      category: 'AutoFleetGenerator',
                      action: 'DemandBasedAllocationToggled',
                      label: isEnabled ? 'Enabled' : 'Disabled',
                    });
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('autoFleetGen.configuration.advanced.demandBased')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('autoFleetGen.configuration.advanced.demandBasedHelper')}
                  </Typography>
                </Box>
              }
            />

            {config.useDemandBasedAllocation && (
              <>
                <Alert severity="success" icon={<Icon size={20} d={ICON_FALLBACKS.TrendingUp} />}>
                  <Typography variant="body2">
                    {t('autoFleetGen.configuration.advanced.demandBasedEnabledAlert')}
                  </Typography>
                </Alert>

                {/* Hourly Vehicle Requirements Editor */}
                <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon name="bus" size={20} />
                      <Typography variant="subtitle2">{t('autoFleetGen.configuration.advanced.hourlyRequirements')}</Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      {analysisResult?.hourlyVehicleRequirements && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            if (analysisResult?.hourlyVehicleRequirements) {
                              onConfigChange({ 
                                hourlyVehicleRequirements: { ...analysisResult.hourlyVehicleRequirements }
                              });
                            }
                          }}
                        >
                          {t('autoFleetGen.configuration.advanced.resetToTripSet')}
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const baseCount = config.manualVehicleCount || 5;
                          const requirements: Record<number, number> = {};
                          for (let hour = 0; hour < 24; hour++) {
                            requirements[hour] = baseCount;
                          }
                          onConfigChange({ hourlyVehicleRequirements: requirements });
                        }}
                      >
                        {t('autoFleetGen.configuration.advanced.resetAllTo', { value: config.manualVehicleCount || 5 })}
                      </Button>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    {analysisResult
                      ? t('autoFleetGen.configuration.advanced.hourlyDescriptionAnalysis')
                      : t('autoFleetGen.configuration.advanced.hourlyDescription')}
                  </Typography>

                  {/* Visual Preview of Hourly Requirements */}
                  {config.hourlyVehicleRequirements && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#fff', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {t('autoFleetGen.configuration.advanced.visualPreview')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 0.5,
                          height: 60,
                          borderBottom: '1px solid #ddd',
                          pb: 0.5,
                        }}
                      >
                        {Array.from({ length: 24 }, (_, hour) => {
                          const value = config.hourlyVehicleRequirements?.[hour] ?? 0;
                          const maxValue = Math.max(
                            ...Object.values(config.hourlyVehicleRequirements || {}),
                            1
                          );
                          const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
                          
                          return (
                            <Box
                              key={hour}
                              sx={{
                                flex: 1,
                                height: `${heightPercent}%`,
                                bgcolor: value > 0 ? '#1976d2' : '#e0e0e0',
                                borderRadius: '2px 2px 0 0',
                                minHeight: value > 0 ? 4 : 2,
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                '&:hover': {
                                  bgcolor: value > 0 ? '#1565c0' : '#d0d0d0',
                                  transform: 'scaleY(1.1)',
                                },
                              }}
                              title={`${hour.toString().padStart(2, '0')}:00 - ${value} vehicle${value !== 1 ? 's' : ''}`}
                            />
                          );
                        })}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">00:00</Typography>
                        <Typography variant="caption" color="text.secondary">12:00</Typography>
                        <Typography variant="caption" color="text.secondary">23:00</Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Hourly Requirements Grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(8, 1fr)',
                      gap: 1,
                      maxHeight: 'none',
                    }}
                  >
                    {Array.from({ length: 24 }, (_, hour) => {
                      const currentValue = config.hourlyVehicleRequirements?.[hour] ?? 0;
                      const isServiceHour = 
                        hour >= parseInt(config.serviceStartTime.split(':')[0]) &&
                        hour < parseInt(config.serviceEndTime.split(':')[0]);
                      
                      return (
                        <TextField
                          key={hour}
                          label={`${hour.toString().padStart(2, '0')}:00`}
                          type="number"
                          size="small"
                          value={currentValue}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 0) {
                              onConfigChange({
                                hourlyVehicleRequirements: {
                                  ...(config.hourlyVehicleRequirements || {}),
                                  [hour]: value,
                                },
                              });
                            }
                          }}
                          inputProps={{ min: 0, max: config.manualVehicleCount || 99 }}
                          sx={{
                            bgcolor: isServiceHour ? '#fff' : '#f5f5f5',
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              fontWeight: currentValue > 0 ? 600 : 400,
                              color: currentValue > 0 ? 'primary.main' : 'text.secondary',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Quick Actions */}
                  <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const requirements: Record<number, number> = {};
                        const baseCount = config.manualVehicleCount || 5;
                        for (let hour = 0; hour < 24; hour++) {
                          // Low demand outside service hours, base count during service
                          const startHour = parseInt(config.serviceStartTime.split(':')[0]);
                          const endHour = parseInt(config.serviceEndTime.split(':')[0]);
                          requirements[hour] = (hour >= startHour && hour < endHour) ? baseCount : 0;
                        }
                        onConfigChange({ hourlyVehicleRequirements: requirements });
                      }}
                    >
                      {t('autoFleetGen.configuration.advanced.setServiceHoursOnly')}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const requirements: Record<number, number> = {};
                        for (let hour = 0; hour < 24; hour++) {
                          requirements[hour] = 0;
                        }
                        onConfigChange({ hourlyVehicleRequirements: requirements });
                      }}
                    >
                      {t('autoFleetGen.configuration.advanced.clearAll')}
                    </Button>
                  </Box>
                </Paper>
              </>
            )}

            <Divider />

            <TextField
              label={t('autoFleetGen.configuration.advanced.tripsPerVehiclePerHour')}
              type="number"
              fullWidth
              value={config.tripsPerVehiclePerHour}
              onChange={(e) =>
                onConfigChange({ tripsPerVehiclePerHour: parseInt(e.target.value, 10) })
              }
              inputProps={{ min: 1 }}
              helperText={t('autoFleetGen.configuration.advanced.tripsPerVehiclePerHourHelper')}
            />

            {analysisResult && (
              <Alert severity="info">
                <Typography variant="body2">
                  {t('autoFleetGen.configuration.advanced.analysisRecommendation', {
                    trips: analysisResult.totalTrips,
                    peak: analysisResult.peakDemand,
                    vehicles: analysisResult.calculatedVehicles,
                    rate: config.tripsPerVehiclePerHour,
                  })}
                </Typography>
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConfigurationStep;

